import store from "@/stores/index";

declare interface Permission {
  mask: number;
  label: string;
  bindProject: boolean;
}

export const PERMISSIONS: any = {
  LICENSE: { mask: 1 << 0, label: "修改注册码", bindProject: false },
  CUD_USER: { mask: 1 << 1, label: "增删改账号", bindProject: false },
  SYSTEM_LOG: { mask: 1 << 2, label: "查看操作日志", bindProject: false },
  PERMISSION: { mask: 1 << 3, label: "配置权限", bindProject: false },
  CREATE_PROJECT: { mask: 1 << 4, label: "创建项目", bindProject: false },
  MERGE_PROJECT: { mask: 1 << 5, label: "合并项目", bindProject: false },
  UPDATE_PROJECT: { mask: 1 << 6, label: "编辑项目", bindProject: false },
  DELETE_PROJECT: { mask: 1 << 7, label: "删除项目", bindProject: false },
  CLONE_PROJECT: { mask: 1 << 8, label: "复制项目", bindProject: false },
  VIEW_PATENT_LIST: { mask: 1 << 9, label: "查看专利列表", bindProject: true },
  CATEGORY: { mask: 1 << 10, label: "自定义分类", bindProject: true },
  METADATA: { mask: 1 << 11, label: "自定义标引字段", bindProject: true },
  FILTER: { mask: 1 << 12, label: "自定义筛选", bindProject: true },
  IMPORT_PATENT: { mask: 1 << 13, label: "导入专利", bindProject: true },
  EXPORT_PATENT: { mask: 1 << 14, label: "导出专利", bindProject: true },
  DELETE_PATENT: { mask: 1 << 15, label: "删除专利", bindProject: true },
  UPDATE_PATENT_USER: { mask: 1 << 16, label: "修改申请人/发明人", bindProject: true },
  UPDATE_PATENT_CATEGORY: { mask: 1 << 17, label: "修改专利自定义分类", bindProject: true },
  UPDATE_PATENT_METADATA: { mask: 1 << 18, label: "修改专利标引字段", bindProject: true },
  UPDATE_PATENT_LEGAL_STATUS: { mask: 1 << 19, label: "更新专利法律状态", bindProject: true },
  UPDATE_PATENT_FULL_TEXT: { mask: 1 << 20, label: "更新专利全文", bindProject: true },
  VIEW_REPORT: { mask: 1 << 21, label: "查看统计图表", bindProject: true },
  CUSTOM_REPORT: { mask: 1 << 22, label: "自定义统计图表", bindProject: true },
};

class PermissionChecker {
  check(permission: Permission, projectId?: number): boolean {
    const user = store.getters["user/user"] || {};

    const level = user.level;
    if (level === "ADMIN") {
      return true;
    }

    if (!user.perms || user.perms.length === 0) {
      return false;
    }

    let perm = 0;
    if (permission.bindProject) {
      projectId = projectId || 0;
      if (!projectId) {
        const project = (store.state as any).project;
        projectId = project && project.info && project.info.id ? project.info.id : 0;
      }
      if (!projectId) {
        return false;
      }
      perm = user.perms[projectId];
    } else {
      perm = user.perms[0];
    }

    return (perm & permission.mask) > 0;
  }

  canLicense(): boolean {
    return this.check(PERMISSIONS.LICENSE);
  }

  canCudUser(): boolean {
    return this.check(PERMISSIONS.CUD_USER);
  }

  canSystemLog(): boolean {
    return this.check(PERMISSIONS.SYSTEM_LOG);
  }

  canPermission(): boolean {
    return this.check(PERMISSIONS.PERMISSION);
  }

  canCreateProject(): boolean {
    return this.check(PERMISSIONS.CREATE_PROJECT);
  }

  canMergeProject(): boolean {
    return this.check(PERMISSIONS.MERGE_PROJECT);
  }

  canUpdateProject(): boolean {
    return this.check(PERMISSIONS.UPDATE_PROJECT);
  }

  canDeleteProject(): boolean {
    return this.check(PERMISSIONS.DELETE_PROJECT);
  }

  canCloneProject(): boolean {
    return this.check(PERMISSIONS.CLONE_PROJECT);
  }

  canViewPatentList(projectId?: number): boolean {
    return this.check(PERMISSIONS.VIEW_PATENT_LIST, projectId);
  }

  canCategory(projectId?: number): boolean {
    return this.check(PERMISSIONS.CATEGORY, projectId);
  }

  canMetadata(projectId?: number): boolean {
    return this.check(PERMISSIONS.METADATA, projectId);
  }

  canFilter(projectId?: number): boolean {
    return this.check(PERMISSIONS.FILTER, projectId);
  }

  canImportPatent(projectId?: number): boolean {
    return this.check(PERMISSIONS.IMPORT_PATENT, projectId);
  }

  canExportPatent(projectId?: number): boolean {
    return this.check(PERMISSIONS.EXPORT_PATENT, projectId);
  }

  canDeletePatent(projectId?: number): boolean {
    return this.check(PERMISSIONS.DELETE_PATENT, projectId);
  }

  canUpdatePatentUser(projectId?: number): boolean {
    return this.check(PERMISSIONS.UPDATE_PATENT_USER, projectId);
  }

  canUpdatePatentCategory(projectId?: number): boolean {
    return this.check(PERMISSIONS.UPDATE_PATENT_CATEGORY, projectId);
  }

  canUpdatePatentMetadata(projectId?: number): boolean {
    return this.check(PERMISSIONS.UPDATE_PATENT_METADATA, projectId);
  }

  canUpdatePatentLegalStatus(projectId?: number): boolean {
    return this.check(PERMISSIONS.UPDATE_PATENT_LEGAL_STATUS, projectId);
  }

  canUpdatePatentFullText(projectId?: number): boolean {
    return this.check(PERMISSIONS.UPDATE_PATENT_FULL_TEXT, projectId);
  }

  canViewReport(projectId?: number): boolean {
    return this.check(PERMISSIONS.VIEW_REPORT, projectId);
  }

  canCustomReport(projectId?: number): boolean {
    return this.check(PERMISSIONS.CUSTOM_REPORT, projectId);
  }
}

export default new PermissionChecker();
